import { format } from 'date-fns';

import { MeasureDefinition, MeasureType, ReportData } from '../types';
import { formatMeasureValue } from './format-measure';

export const PIVOT_SEP = '__';

export const formatPivotKey = (key: string, dimKey?: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
    const [year, month, day] = key.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (dimKey === 'period_day') {
      return format(date, 'MMM d');
    }
    if (dimKey === 'period_quarter') {
      return `Q${Math.ceil(month / 3)} '${String(year).slice(2)}`;
    }
    return format(date, 'MMM');
  }
  return key;
};

export interface ChartRow {
  __label: string;
  [key: string]: string | number | null;
}

export interface ChartDataResult {
  rows: ChartRow[];
  measures: MeasureDefinition[];
}

export function buildChartData(data: ReportData): ChartDataResult {
  const categoryDim = data.dimensions[0] ?? null;
  const isPivot = !!data.pivotDimension && (data.pivotValues?.length ?? 0) > 0;
  const pvs = data.pivotValues ?? [];

  const pivotDimKey = data.pivotDimension?.key;

  const effectiveMeasures: MeasureDefinition[] = isPivot
    ? data.measures.flatMap(m =>
        pvs.map(pv => ({
          key: `${m.key}${PIVOT_SEP}${pv.key}`,
          label: `${formatPivotKey(pv.label, pivotDimKey)} · ${m.label}`,
          type: m.type,
          dependsOn: m.dependsOn,
        }))
      )
    : data.measures;

  const rows: ChartRow[] = data.rows.map(row => {
    const rawLabel = categoryDim ? row.columns[categoryDim.displayKey] : null;
    const label = rawLabel != null ? formatMeasureValue(rawLabel, categoryDim?.key, '—') : 'Total';

    const entry: ChartRow = { __label: label };
    for (const m of effectiveMeasures) {
      const v = row.columns[m.key];
      const num = typeof v === 'number' ? v : typeof v === 'string' ? parseFloat(v) : null;
      entry[m.key] = num !== null && Number.isFinite(num) ? num : null;
    }
    return entry;
  });

  return { rows, measures: effectiveMeasures };
}

export function buildYAxisFormatter(type: string | undefined): (value: number) => string {
  return (value: number) => {
    switch (type) {
      case MeasureType.HOURS:
        return `${Number(value).toFixed(0)}h`;
      case MeasureType.MONEY:
        return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : String(Math.round(value));
      case MeasureType.PERCENT:
        return `${Number(value).toFixed(0)}%`;
      default:
        return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : String(Math.round(value));
    }
  };
}

export function buildTooltipFormatter(
  data: ReportData
): (value: number, name: string) => [string, string] {
  const isPivot = !!data.pivotDimension && (data.pivotValues?.length ?? 0) > 0;
  const pvs = data.pivotValues ?? [];
  const pivotDimKey = data.pivotDimension?.key;

  const measureByKey = new Map(data.measures.map(m => [m.key, m]));

  // Pre-build a label map for pivot keys: "tracked_hours__2026-08-01" → "Aug · Tracked"
  const pivotLabelMap = new Map<string, string>();
  if (isPivot) {
    for (const m of data.measures) {
      for (const pv of pvs) {
        const colKey = `${m.key}${PIVOT_SEP}${pv.key}`;
        pivotLabelMap.set(colKey, `${formatPivotKey(pv.label, pivotDimKey)} · ${m.label}`);
      }
    }
  }

  return (value: number, name: string) => {
    if (isPivot) {
      const label = pivotLabelMap.get(name) ?? name;
      // Derive measure type from the base measure key
      const baseMeasureKey = name.split(PIVOT_SEP)[0];
      const measure = measureByKey.get(baseMeasureKey);
      return [formatMeasureValue(value, measure?.type), label];
    }
    const measure = measureByKey.get(name);
    return [formatMeasureValue(value, measure?.type), measure?.label ?? name];
  };
}
